/* בית: תוכנית יציאה מהמינוס ← כרית ביטחון ← חיסכון */
import { useState } from 'react';
import { Icon } from '../../components/Icon.jsx';
import { S } from '../../domain/state.js';
import { fMoney, fShort, wD, wDYY, fISO } from '../../lib/format.js';
import { exitPlan, safeToSpend, savPeriod, histPeriods, bufSuggest } from '../../domain/plan.js';
import { Xn, promptDialog } from '../../components/ui.jsx';
import { navigate } from '../../app/nav.js';
import { setExitTarget, setBuffer } from '../../store/actions.js';
import { openGoalForm } from '../goals/GoalsPage.jsx';

const STEPS = ['סוף התקופה', 'אמצע החודש', 'כרית ביטחון', 'חיסכון'];

function Stepper({ st }) {
  return (
    <div className="plan-steps">
      {STEPS.map((n, i) => (
        <div key={i} className={'plan-step ' + (i + 1 < st ? 'done' : i + 1 === st ? 'on' : '')}>
          <i>{i + 1 < st ? '✓' : i + 1}</i><span>{n}</span>
        </div>
      ))}
    </div>
  );
}

async function customBuffer() {
  const v = await promptDialog({
    title: 'גודל כרית הביטחון', type: 'number', okLabel: 'שמור',
    body: 'הסכום שתרצה שיישאר בעו"ש גם ביום הכי נמוך בחודש:',
    initial: S.set.bufTarget > 0 ? S.set.bufTarget : bufSuggest(),
  });
  const n = parseFloat(v);
  if (n > 0) setBuffer(n);
}

/** המטרה של השלב, במילים פשוטות, והמספר שמודד אותה */
function Goal({ P }) {
  const abs = (n) => fMoney(Math.abs(n));
  if (P.stage === 1) return (
    <>
      <div className="plan-goal">לסיים את התקופה בלי מינוס</div>
      <div className="sub">ב-{wD(P.endDate)}, אחרי המשכורת וחיוב הכרטיס, צפויים <b className="red"><Xn k="end">{fMoney(P.end)}</Xn></b>. חסרים <Xn k="need">{abs(P.debt)}</Xn>.</div>
      {P.low < P.end - 100 && <div className="note mt6">ובאמצע החודש: ב-{wD(P.lowDate)} החשבון יורד ל-<b className="red"><Xn k="low">{fMoney(P.low)}</Xn></b>. זה השלב הבא.</div>}
    </>
  );
  if (P.stage === 2) return (
    <>
      <div className="plan-goal">שגם לפני המשכורת לא תהיו במינוס</div>
      <div className="sub">סוף התקופה כבר חיובי, אבל ב-{wD(P.lowDate)} החשבון יורד ל-<b className="red"><Xn k="low">{fMoney(P.low)}</Xn></b>. חסרים <Xn k="need">{abs(P.need)}</Xn>.</div>
    </>
  );
  if (P.stage === 3) return (
    <>
      <div className="plan-goal">כרית ביטחון</div>
      <div className="sub">{P.bufUnset ? 'יצאתם מהמינוס, גם באמצע החודש. עכשיו: סכום שיישאר בחשבון גם ביום הכי נמוך, כדי שחודש חריג לא יחזיר אתכם למינוס.'
        : <>שיישארו <Xn k="buf">{fMoney(P.buf)}</Xn> גם ביום הכי נמוך. היום הכי נמוך עכשיו: <Xn k="low">{fMoney(P.low)}</Xn> ב-{wD(P.lowDate)}. חסרים <Xn k="need">{fMoney(P.need)}</Xn>.</>}</div>
    </>
  );
  return <div className="plan-goal">חיסכון</div>;
}

/** הצעד הבא: כמה בחודש ועד מתי, או בכמה זמן זה ייסגר לבד */
function NextStep({ P }) {
  const [pick, setPick] = useState(false);
  if (P.stage === 4 || (P.stage === 3 && P.bufUnset)) return null;
  const hasHist = histPeriods() >= 2;
  if (P.target) {
    const s = safeToSpend(), onTrack = s ? s.left >= (s.usualRest || 0) : P.gap <= 0;
    return (
      <div className="plan-next">
        <div className="row-b" style={{ alignItems: 'center', gap: 8 }}>
          <div>
            <b>לחסוך <Xn k="save">{fMoney(P.required)}</Xn> בחודש</b>
            <div className="note">עד {wDYY(new Date(P.target + 'T12:00:00'))} · <button className="link-muted" onClick={() => setExitTarget('')}>לשנות</button></div>
          </div>
          {(hasHist || (s && s.left < 0)) && <span className={'hero-chip ' + (onTrack ? 'chip-ok' : 'chip-warn')}>{onTrack ? '✓ בדרך' : 'צריך לקצץ'}</span>}
        </div>
        {s && !onTrack && <div className="note mt6">{s.left < 0 ? 'גם בלי הוצאות נוספות החודש לא מגיעים. צריך הכנסה נוספת או לדחות חיוב.' : 'כדי להספיק, צריך לעמוד ב"מותר להוציא היום" למעלה.'}</div>}
      </div>
    );
  }
  return (
    <div className="plan-next">
      {hasHist ? (P.surplus > 0 && P.eta
        ? <div>בקצב של היום (עודף של <Xn k="surplus">{fMoney(P.surplus)}</Xn> בחודש) זה ייסגר בערך ב-<b>{wDYY(P.eta)}</b>.</div>
        : <div><b className="red">בחודש רגיל אין עודף</b> (<Xn k="surplus">{fMoney(P.surplus)}</Xn>), ולכן זה לא ייסגר לבד. כדאי לבדוק למטה לאן הולך הכסף.</div>)
        : <div className="note">כדי לדעת בכמה זמן זה ייסגר, צריך לסרוק את העו"ש של שתי התקופות הקודמות.</div>}
      {!pick ? <button className="link mt6" style={{ fontSize: '.9rem' }} onClick={() => setPick(true)}>לקבוע תאריך יעד</button> : (
        <>
          <div className="row mt10">
            {[3, 6, 12].map((m) => {
              const d = new Date(); d.setMonth(d.getMonth() + m);
              return <button key={m} className="ubtn ubtn-outline" style={{ flex: 1 }} onClick={() => setExitTarget(fISO(d))}>{m === 12 ? 'שנה' : m + ' חודשים'}</button>;
            })}
          </div>
          <input type="date" onChange={(e) => e.target.value && setExitTarget(e.target.value)} style={{ width: '100%', marginTop: 8 }} aria-label="תאריך יעד" />
        </>
      )}
    </div>
  );
}

export function ExitPlan() {
  const [open, setOpen] = useState(false);
  const P = exitPlan();
  if (!P) return null;
  return (
    <div className="period">
      <div className="row-b" style={{ alignItems: 'baseline', marginBottom: 10 }}>
        <span className="sec" style={{ margin: 0 }}>🎯 המטרה עכשיו</span>
        <span className="sec-sub">שלב {P.stage} מתוך 4</span>
      </div>
      <Stepper st={P.stage} />
      <Goal P={P} />
      {P.stage === 4 || (P.stage === 3 && P.bufUnset) ? <PlanBody P={P} /> : <NextStep P={P} />}
      {P.stage < 4 && !(P.stage === 3 && P.bufUnset) && (
        <>
          <button className="link-muted mt10" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? 'פחות' : 'עוד פרטים'} <Icon name="chevD" size={14} style={open ? { transform: 'rotate(180deg)' } : undefined} />
          </button>
          {open && <><PlanBody P={P} details /><Trend /></>}
        </>
      )}
    </div>
  );
}

function PlanBody({ P, details }) {
  if (P.stage === 4) return <SavingsPlan P={P} />;
  if (details) return (
    <div className="note mt6">
      {P.stage === 1 && P.swing > 0 && <div>אחרי שסוף התקופה יהיה חיובי: כדי שגם באמצע החודש לא יהיה מינוס, צריך לצבור עוד {fMoney(P.swing)}. זה הפער בין היום הכי נמוך לסוף התקופה.</div>}
      {P.stage === 3 && <div><button className="link" onClick={customBuffer}>לשנות את גודל הכרית</button> · <button className="link-muted" onClick={() => setBuffer(-1)}>לדלג לחיסכון</button></div>}
      <div className="mt6">השלבים: 1. סוף התקופה בלי מינוס. 2. גם היום הכי נמוך בלי מינוס. 3. כרית ביטחון. 4. חיסכון.</div>
    </div>
  );
  if (P.stage === 3 && P.bufUnset) {
    const s = bufSuggest();
    return (
      <>
        <div className="sub mt10">כמה?</div>
        <div className="row mt6" style={{ gap: 6 }}>
          {[[0.5, 'חצי חודש'], [1, 'חודש'], [2, 'חודשיים']].map(([m, l]) => {
            const v = Math.round((s * m) / 500) * 500;
            return (
              <button key={m} className={'ubtn ' + (m === 1 ? 'ubtn-primary' : 'ubtn-outline')} style={{ flex: 1, padding: '8px 4px', lineHeight: 1.3, flexDirection: 'column', gap: 2, fontSize: '.9rem' }} onClick={() => setBuffer(v)}>
                <span>{l}</span><small style={{ fontWeight: 600, opacity: 0.85 }}>{fMoney(v)}</small>
              </button>
            );
          })}
        </div>
        <div className="row-b mt6" style={{ fontSize: '.86rem' }}>
          <span className="muted"><Xn k="buf">חודש = יציאות של חודש רגיל</Xn></span>
          <span><button className="link" onClick={customBuffer}>סכום אחר</button> · <button className="link-muted" onClick={() => setBuffer(-1)}>דלג לחיסכון</button></span>
        </div>
      </>
    );
  }
  return null;
}

/** שלב 4: כמה לחסוך החודש, כמה כבר הועבר, ומצב היעדים */
function SavingsPlan({ P }) {
  const SP = savPeriod();
  const intro = (
    <div className="sub mt10" style={{ fontSize: '.93rem' }}>
      <b>✓ יצאת מהמינוס{P.buf ? ' ויש כרית ביטחון' : ''}.</b> עכשיו כל עודף יכול ללכת לחיסכון. בחודש רגיל נשאר כ-<Xn k="surplus">{fMoney(P.surplus)}</Xn>.
      {histPeriods() < 2 && <span className="muted"> זו הערכה ראשונית. אחרי סריקת החודשיים הקודמים היא תהיה מדויקת יותר.</span>}
    </div>
  );
  if (!SP.goals.length) {
    const sug = Math.max(0, Math.floor((P.surplus * 0.7) / 100) * 100);
    return (
      <>
        {intro}
        <div className="alert-green mt10" style={{ fontSize: '.9rem', lineHeight: 1.6 }}>
          עוד אין יעד חיסכון. {sug ? <>יעד סביר להתחלה: עד <b>{fMoney(sug)}</b> בחודש, כ-70% מהעודף, כדי להשאיר מרווח לחודשים חריגים.</> : null}
          <button className="ubtn ubtn-primary w100 mt6" onClick={() => { navigate('Goals'); openGoalForm(); }}>🌱 יעד חיסכון ראשון</button>
        </div>
      </>
    );
  }
  const got = SP.done + SP.planned, pct = SP.M > 0 ? Math.min(100, (got / SP.M) * 100) : 0;
  return (
    <>
      {intro}
      {SP.M > 0 ? (
        <div className="mt12">
          <div className="row-b" style={{ alignItems: 'baseline', fontSize: '.9rem', color: 'var(--ink2)' }}>
            <span>החודש לחיסכון</span>
            <span><Xn k="sav_month" arg="got">{fMoney(got)}</Xn> מתוך <Xn k="sav_month" arg="target">{fMoney(SP.M)}</Xn></span>
          </div>
          <div className="goal-progress" style={{ margin: '6px 0' }}><div className="goal-progress-fill" style={{ width: pct + '%', background: 'var(--green)' }} /></div>
          <div className="note">
            {SP.left > 0 ? 'נשאר להעביר החודש ' + fMoney(SP.left) + '. הסכום הזה כבר שמור בצד ב"אפשר להוציא" למעלה.' : '✓ כל החיסכון של החודש הועבר או מתוכנן.'}
            {SP.planned ? ' ' + fMoney(SP.planned) + ' מתוכנן כהוראת קבע.' : ''}
            {SP.M > P.surplus && <b className="red"> היעדים דורשים יותר מהעודף החודשי.</b>}
          </div>
        </div>
      ) : <div className="note mt6">ליעדים אין תאריך או סכום חודשי, ולכן אין יעד לחודש הזה. אפשר להגדיר בעריכת היעד.</div>}
      <div className="mt10">
        {SP.goals.slice(0, 3).map((s) => (
          <div key={s.g.id} className="clickable" onClick={() => navigate('Goals')} style={{ padding: '7px 0', borderTop: '1px solid var(--border)' }}>
            <div className="row-b" style={{ fontSize: '.9rem' }}>
              <span>{s.g.emoji} {s.g.name}</span>
              <span><b>{fMoney(s.total)}</b> <span className="muted">/ {fMoney(s.target)}</span></span>
            </div>
            <div className="goal-progress" style={{ margin: '5px 0 0', height: 5 }}><div className="goal-progress-fill" style={{ width: s.pct + '%', background: s.g.color || 'var(--green)' }} /></div>
          </div>
        ))}
      </div>
    </>
  );
}

/** היתרה בסוף כל תקופה, לפי מה שנראה בזמן אמת */
function Trend() {
  const hist = S.set.exitEnd || {};
  const hk = Object.keys(hist).sort().slice(-4);
  if (hk.length < 2) return null;
  return (
    <div className="row mt10" style={{ flexWrap: 'wrap', gap: 6, paddingTop: 9, borderTop: '1px solid var(--border)', fontSize: '.86rem', color: 'var(--ink3)' }}>
      <span>סוף התקופה, לאורך זמן:</span>
      {hk.map((k, i) => {
        const v = hist[k], prev = i ? hist[hk[i - 1]] : null;
        return <span key={k}>{i > 0 && ' → '}<b style={{ color: prev == null ? 'var(--ink2)' : v > prev ? 'var(--green)' : 'var(--red)' }}>{fShort(v)}</b></span>;
      })}
    </div>
  );
}

